import { connect, ConnectedProps } from 'react-redux';

import { FormCreateTransactionTypeData } from 'dashboard/forms/FormCreateTransactionType/FormCreateTransactionType';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setFilters } from 'dashboard/redux/actions/transaction-types';
import {
  useActivateTransactionTypeMutation,
  useCreateTransactionTypeMutation,
  useDeactivateTransactionTypeMutation,
} from 'dashboard/redux/api/transaction-type';
import { RootState } from 'dashboard/redux/store';
import { loadTransactionTypesFilterOptions } from 'dashboard/redux/thunk-actions/transaction-types';
import DashboardTransactionTypesView from 'dashboard/views/DashboardTransactionTypesView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardTransactionTypesRoute = (props: PropsFromRedux) => {
  const { loading: isLoading, callback: loadTransactionTypeFilters } = useApiLoader({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    loader: async () => await props.loadTransactionTypesFilterOptions({ filters: props.filters }),
    deps: [props.loadTransactionTypesFilterOptions, props.filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load transaction types.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const [
    createTransactionType,
    { isLoading: isCreateTransactionTypeLoading, error: createTransactionTypeError },
  ] = useCreateTransactionTypeMutation();

  const handleCreateTransactionType = async (formData: FormCreateTransactionTypeData) => {
    await createTransactionType({
      name: formData['name'],
      environmentId: props.currentEnvironment.id,
    });
    props.showMessage('Successfully created transaction type.', MessageType.Success, true);
    await loadTransactionTypeFilters();
  };

  useRTKErrorHandler({
    error: createTransactionTypeError,
    onError: () => ({
      message: 'Failed to create transaction type.',
      level: MessageType.Failure,
    }),
  });

  const [
    deactivateTransactionType,
    { isLoading: deactivateTransactionTypeLoading, error: deactivateTransactionTypeError },
  ] = useDeactivateTransactionTypeMutation();

  const handleDeactivateTransactionType = async (transactionTypeId: string) => {
    await deactivateTransactionType(transactionTypeId);
    await loadTransactionTypeFilters();
  };

  useRTKErrorHandler({
    error: deactivateTransactionTypeError,
    onError: () => ({
      message: 'Failed to deactivate transaction type. Please try again later.',
      level: MessageType.Failure,
    }),
  });

  const [
    activateTransactionType,
    { isLoading: activateTransactionTypeLoading, error: activateTransactionTypeError },
  ] = useActivateTransactionTypeMutation();

  const handleActivateTransactionType = async (transactionTypeId: string) => {
    await activateTransactionType(transactionTypeId);
    await loadTransactionTypeFilters();
  };

  useRTKErrorHandler({
    error: activateTransactionTypeError,
    onError: () => ({
      message: 'Failed to activate transaction type. Please try again later.',
      level: MessageType.Failure,
    }),
  });

  const handleStatusFilterUpdate = (statuses: TransactionTypeStatus[]) => {
    props.setFilters({ status: statuses });
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { handleOmniSearch } = useTable({ filters: props.filters, setFilters: props.setFilters });

  return (
    <DashboardTransactionTypesView
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      isLoading={
        isLoading ||
        isCreateTransactionTypeLoading ||
        activateTransactionTypeLoading ||
        deactivateTransactionTypeLoading
      }
      transactionTypeBasics={props.transactionTypeBasics}
      createTransactionType={handleCreateTransactionType}
      handleActivateTransactionType={handleActivateTransactionType}
      handleDeactivateTransactionType={handleDeactivateTransactionType}
      handleOmniSearch={handleOmniSearch}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    filters: state.transactionTypes.filters,
    transactionTypeBasics: state.transactionTypes.filterOptions,
  };
};
const mapDispatchToProps = {
  loadTransactionTypesFilterOptions,
  showMessage,
  setFilters,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTransactionTypesRoute);
